import React, { useState, useEffect } from "react";
import { FaGithub, FaLinkedin } from "react-icons/fa";
import "./Navbar.css";
import logo from "./img/logo.png"; // Your logo path

function Navbar({ onModalClose }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("Home");

  useEffect(() => {
    const handleScroll = () => {
      // Use different scroll thresholds based on device size
      const isMobile = window.innerWidth <= 768;
      const scrollThreshold = isMobile ? 5 : 70;
      setScrolled(window.scrollY > scrollThreshold);
    };
    
    // Check initial scroll position immediately to handle page reloads
    handleScroll();
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Active section detection using scroll position
  useEffect(() => {
    const sections = ["Home", "About", "Skills", "Portfolio", "Services", "Contact"];
    
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      
      // Special handling for Home section (at the top)
      if (scrollPosition < 200) {
        setActiveSection("Home");
        return;
      }
      
      // Check other sections first (excluding Contact)
      const sectionsToCheck = sections.filter(section => section !== "Contact");
      for (let i = sectionsToCheck.length - 1; i >= 0; i--) {
        const section = document.getElementById(sectionsToCheck[i]);
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionHeight = section.offsetHeight;
          const sectionBottom = sectionTop + sectionHeight;
          
          // Check if scroll position is within this section (reduced offset)
          if (scrollPosition >= sectionTop - 50 && scrollPosition < sectionBottom - 50) {
            setActiveSection(sectionsToCheck[i]);
            return;
          }
        }
      }
      
      // Special handling for Contact section (only if we're actually near the bottom)
      const documentHeight = document.documentElement.scrollHeight;
      const contactSection = document.getElementById("Contact");
      if (contactSection) {
        const contactTop = contactSection.offsetTop;
        // Only highlight Contact if we're actually in the Contact section area
        if (scrollPosition >= contactTop - 100) {
          setActiveSection("Contact");
          return;
        }
      }
      
      // Fallback: if we're at the very bottom of the page
      if (scrollPosition + windowHeight >= documentHeight - 50) {
        setActiveSection("Contact");
        return;
      }
    };

    // Set initial active section
    handleScroll();
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const closeMenuOnClickOutside = (event) => {
      if (menuOpen && !event.target.closest(".mobile__menu") && !event.target.closest(".menu__toggle")) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("click", closeMenuOnClickOutside);
    return () => document.removeEventListener("click", closeMenuOnClickOutside);
  }, [menuOpen]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const hash = window.location.hash.substring(1);
      if (hash) {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Handle navigation clicks - close modal and scroll to section
  const handleNavClick = (sectionName) => {
    // Close mobile menu if open
    setMenuOpen(false);
    
    // Update URL hash
    window.history.pushState(null, null, `#${sectionName}`);
    
    // Close modal if it's open
    if (onModalClose) {
      // Mark that modal is being closed via navigation
      window.modalIsOpen = false;
      onModalClose();
      
      // Wait for modal to close and then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionName);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 150); // Shorter delay, use standard scrollIntoView
    } else {
      // If no modal is open, scroll immediately
      const element = document.getElementById(sectionName);
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };

  return (
    <header className={`header ${scrolled ? "scrolled" : ""}`}>
      <div className="header__container">
        {/* Logo */}
        <a href="#Home" className="logo" onClick={(e) => { e.preventDefault(); handleNavClick('Home'); }}>
          <img src={logo} alt="Website Logo" />
        </a>

        {/* Navigation */}
        <nav className="navigation">
          <ul className="navbar">
            {["Home", "About", "Skills", "Portfolio", "Services", "Contact"].map((item) => (
              <li key={item}>
                <a 
                  href={`#${item}`} 
                  className={`nav__link ${activeSection === item ? 'active' : ''}`}
                  onClick={(e) => { e.preventDefault(); handleNavClick(item); }}
                >
                  {item}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Social Links */}
        <div className="social__links">
          <a href="https://github.com/Lyam0udi" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
            <FaGithub />
          </a>
          <a href="https://www.linkedin.com/in/Lyam0udi/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
            <FaLinkedin />
          </a>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="menu__toggle"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-expanded={menuOpen}
          aria-label="Toggle navigation menu"
        >
        </button>
      </div>

      {/* Mobile Menu */}
      <nav className={`mobile__menu ${menuOpen ? "open" : ""}`} aria-hidden={!menuOpen}>
        <button className="mobile__close" onClick={() => setMenuOpen(false)} aria-label="Close navigation menu"></button>
        <ul>
          {["Home", "About", "Skills", "Portfolio", "Services", "Contact"].map((item) => (
            <li key={item}>
              <a 
                href={`#${item}`} 
                onClick={(e) => { e.preventDefault(); handleNavClick(item); }}
                className={activeSection === item ? 'active' : ''}
              >
                {item}
              </a>
            </li>
          ))}

          {/* Social Links Inside Mobile Menu */}
          <li className="social__links">
            <a href="https://github.com/Lyam0udi" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
              <FaGithub />
            </a>
            <a href="https://www.linkedin.com/in/Lyam0udi/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <FaLinkedin />
            </a>
          </li>
        </ul>
      </nav>
    </header>
  );
}

export default Navbar;
